/**
 * Maps backend extraction keys from the CCA (Credit Agreement) AI extractor
 * to the ClosedDealStepper form structure.
 *
 * The extraction response uses `containers[]` where:
 *   - Single facility: containers[0].data holds all fields
 *   - Multiple facilities: containers[0] = parent deal, containers[1..N] = sub-facilities
 *
 * Each container's `data` uses backend-namespaced keys (e.g. `c.oAmt`, `c.trm`).
 * This utility converts them into partial form patches grouped by stepper step.
 */

/** Extracted data for a single sub-facility */
export interface SubFacilityExtraction {
  name: string;
  productType: string;
  rateType: string;
  amount: number | null;
  term: number | null;
  amortization: number | null;
  interestOnlyPeriod: number | null;
  closedDate: Date | null;
  maturityDate: Date | null;
  prepayment: string;
  expectedUtilization: string;
  fixedRate: number | null;
  index: string;
  spread: number | null;
  rateFloorType: string;
  floor: number | null;
}

export interface CcaExtractionResult {
  setup: Record<string, any>;
  structure: Record<string, any>;
  borrower: Record<string, any>;
  pricing: Record<string, any>;
  collateral: Record<string, any>;
  cre: Record<string, any>;
  dealName: string | null;
  filledFieldCount: number;
  /** Set of "formGroup.fieldName" strings for all fields that were extracted */
  filledFields: Set<string>;
  /** Sub-facilities extracted from containers[1..N] */
  subFacilities: Partial<SubFacilityExtraction>[];
}

interface FieldMapping {
  form: string;
  field: string;
  type: 'string' | 'number' | 'date';
}

/**
 * Maps extraction container data keys → stepper form group + field.
 * Keys must match what the backend CCA extractor returns in `containers[0].data`.
 */
const CCA_FIELD_MAP: Record<string, FieldMapping> = {
  // Setup
  'c.sTp': { form: 'setup', field: 'transactionType', type: 'string' },
  'c.loanPurpose': { form: 'setup', field: 'loanPurpose', type: 'string' },
  'c.numberOfLenders': { form: 'setup', field: 'numLenders', type: 'number' },
  // Structure
  'c.pr': { form: 'structure', field: 'productType', type: 'string' },
  'f.interestRateType': {
    form: 'structure',
    field: 'rateType',
    type: 'string',
  },
  'c.oAmt': { form: 'structure', field: 'amount', type: 'number' },
  'c.trm': { form: 'structure', field: 'term', type: 'number' },
  'c.amtz': { form: 'structure', field: 'amortization', type: 'number' },
  't.iop': {
    form: 'structure',
    field: 'interestOnlyPeriod',
    type: 'number',
  },
  'c.cDt': { form: 'structure', field: 'closedDate', type: 'date' },
  'a.mDt': { form: 'structure', field: 'maturityDate', type: 'date' },
  // Borrower
  'c.indu': { form: 'borrower', field: 'industry', type: 'string' },
  'c.usState': { form: 'borrower', field: 'usState', type: 'string' },
  'b.borrowerRevenueSize': {
    form: 'borrower',
    field: 'revenueSize',
    type: 'string',
  },
  'b.privateEquitySponsored': {
    form: 'borrower',
    field: 'peSponsored',
    type: 'string',
  },
  // Pricing
  'f.prepaymentPenaltyFee': {
    form: 'pricing',
    field: 'prepayment',
    type: 'string',
  },
  'c.expectedUtilization': {
    form: 'pricing',
    field: 'expectedUtilization',
    type: 'string',
  },
  'c.initInrRte': { form: 'pricing', field: 'fixedRate', type: 'number' },
  'c.sprd': { form: 'pricing', field: 'spread', type: 'number' },
  'c.indx': { form: 'pricing', field: 'index', type: 'string' },
  'f.interestRateFloorType': {
    form: 'pricing',
    field: 'rateFloorType',
    type: 'string',
  },
  'f.whatIsTheFloorRate': {
    form: 'pricing',
    field: 'floor',
    type: 'number',
  },
  'f.origFee': { form: 'pricing', field: 'originationFee', type: 'number' },
  'f.performanceBasedPricing': {
    form: 'pricing',
    field: 'performancePricing',
    type: 'string',
  },
  // Collateral (BE alias: c1 = io.synply.v1.model.core.collateral)
  'c1.recourseLevel': {
    form: 'collateral',
    field: 'recourseLevel',
    type: 'string',
  },
  'g.guarantorLiabilityBurndown': {
    form: 'collateral',
    field: 'guarantorBurndown',
    type: 'string',
  },
  'f.interestRateHedge': {
    form: 'collateral',
    field: 'hedge',
    type: 'string',
  },
  // CRE
  'c.crePropertyClass': {
    form: 'cre',
    field: 'propertyClass',
    type: 'string',
  },
  'c.projectType': { form: 'cre', field: 'projectType', type: 'string' },
  'c1.loanToValue': { form: 'cre', field: 'ltv', type: 'number' },
  'c1.loanToCost': { form: 'cre', field: 'ltc', type: 'number' },
  'c1.debtYield': { form: 'cre', field: 'debtYield', type: 'number' },
};

function parseNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return isNaN(n) ? null : n;
}

function parseDate(value: unknown): Date | null {
  if (value == null || value === '') return null;
  const d = new Date(String(value));
  return isNaN(d.getTime()) ? null : d;
}

function parseString(value: unknown): string | null {
  if (value == null || value === '') return null;
  return String(value);
}

/** Sub-facility field mapping (subset of main fields relevant to each tranche) */
const SUB_FACILITY_FIELD_MAP: Record<
  string,
  { field: keyof SubFacilityExtraction; type: 'string' | 'number' | 'date' }
> = {
  'c.pr': { field: 'productType', type: 'string' },
  'f.interestRateType': { field: 'rateType', type: 'string' },
  'c.oAmt': { field: 'amount', type: 'number' },
  'c.trm': { field: 'term', type: 'number' },
  'c.amtz': { field: 'amortization', type: 'number' },
  't.iop': { field: 'interestOnlyPeriod', type: 'number' },
  'c.cDt': { field: 'closedDate', type: 'date' },
  'a.mDt': { field: 'maturityDate', type: 'date' },
  'f.prepaymentPenaltyFee': { field: 'prepayment', type: 'string' },
  'c.expectedUtilization': { field: 'expectedUtilization', type: 'string' },
  'c.initInrRte': { field: 'fixedRate', type: 'number' },
  'c.indx': { field: 'index', type: 'string' },
  'c.sprd': { field: 'spread', type: 'number' },
  'f.interestRateFloorType': { field: 'rateFloorType', type: 'string' },
  'f.whatIsTheFloorRate': { field: 'floor', type: 'number' },
};

function parseValue(
  rawValue: string | null | undefined,
  type: 'string' | 'number' | 'date'
): any {
  if (rawValue == null || rawValue === '') return null;
  switch (type) {
    case 'number':
      return parseNumber(rawValue);
    case 'date':
      return parseDate(rawValue);
    default:
      return parseString(rawValue);
  }
}

/**
 * Extract a single sub-facility from a container's data map.
 */
function extractSubFacility(
  data: Record<string, string | null>
): Partial<SubFacilityExtraction> {
  const sf: Partial<SubFacilityExtraction> = {};
  sf.name = parseString(data['dealName'] ?? data['name']) ?? '';

  for (const [key, mapping] of Object.entries(SUB_FACILITY_FIELD_MAP)) {
    const parsed = parseValue(data[key], mapping.type);
    if (parsed != null) {
      (sf as any)[mapping.field] = parsed;
    }
  }

  return sf;
}

/**
 * Transform raw extraction containers into stepper-ready form patches.
 *
 * - Single container  → populates parent deal forms only
 * - Multiple containers → containers[0] = parent, containers[1..N] = sub-facilities
 */
export function applyCcaExtraction(
  data: Record<string, string | null>,
  additionalContainers?: Record<string, string | null>[]
): CcaExtractionResult {
  const result: CcaExtractionResult = {
    setup: {},
    structure: {},
    borrower: {},
    pricing: {},
    collateral: {},
    cre: {},
    dealName: parseString(data['dealName'] ?? data['name']),
    filledFieldCount: 0,
    filledFields: new Set<string>(),
    subFacilities: [],
  };

  for (const [key, mapping] of Object.entries(CCA_FIELD_MAP)) {
    const parsed = parseValue(data[key], mapping.type);
    if (parsed != null) {
      (result as any)[mapping.form][mapping.field] = parsed;
      result.filledFields.add(`${mapping.form}.${mapping.field}`);
      result.filledFieldCount++;
    }
  }

  // Deal name is special — it's in setup.dealName
  if (result.dealName) {
    result.setup['dealName'] = result.dealName;
    result.filledFields.add('setup.dealName');
    result.filledFieldCount++;
  }

  // Process additional containers as sub-facilities
  if (additionalContainers?.length) {
    result.setup['hasSubFacilities'] = true;
    result.setup['numSubFacilities'] = additionalContainers.length;
    result.filledFields.add('setup.hasSubFacilities');
    result.filledFields.add('setup.numSubFacilities');
    result.filledFieldCount += 2;

    for (const containerData of additionalContainers) {
      const sf = extractSubFacility(containerData);
      result.subFacilities.push(sf);
    }
  }

  return result;
}
